/**
 * STRATIFY DESIGN TOKENS — the single source of truth for the product's visual
 * language (Design Constitution §04, §06, §07, §08, §32, §36).
 *
 * WHY this sits beside `theme.tsx` rather than inside it:
 * `theme.tsx` is the tenant-facing PRESET system (blue / dark / green / purple
 * / orange) — a white-label surface a customer can repaint. These tokens are
 * the PRODUCT's identity: the brand ramp, the navigation navy, the AI violet,
 * the radius scale and the motion curve. They must not change when a tenant
 * picks a preset, or Stratify stops looking like Stratify.
 *
 * The division of labour on any page:
 *   surfaces + body text  → MUI theme (`background.paper`, `text.secondary`,
 *                           `divider`) so the dark preset keeps working
 *   identity + meaning    → these tokens (ramp, AI violet, semantic status)
 *
 * Never hardcode a hex in a page. Import it from here.
 */

// ── Colour ─────────────────────────────────────────────────────────────────
export const COLOR = {
  /** Foundation. Light workspace, per §04. */
  white: '#FFFFFF',
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',

  /** Primary. */
  primary: '#2563EB',
  /** Secondary / structure. */
  indigo: '#4F46E5',
  indigoLight: '#6366F1',

  /** AI / intelligence. Reserved — never use violet for a non-AI surface, or
   *  the "this is Stratify AI" signal stops meaning anything (§48.9). */
  ai: '#7C3AED',
  aiLight: '#8B5CF6',
  aiGlow: '#C4B5FD',

  /** Semantic. Colour must communicate meaning, never decorate (§04). */
  success: '#16A34A',
  warning: '#F59E0B',
  danger: '#DC2626',

  /** Navigation. Fixed across every preset. */
  navy: '#0B1220',
  navyRaised: '#0F172A',

  /** Text. */
  ink: '#0F172A',
  inkMuted: '#64748B',
} as const;

// ── Gradients ──────────────────────────────────────────────────────────────
/** Blue → indigo → violet. The only decorative ramp in the product (§48.8). */
export const GRADIENT = {
  ramp: 'linear-gradient(135deg, #6366F1 0%, #2563EB 55%, #8B5CF6 100%)',
  /** Deep surface for premium / featured / AI panels. */
  night: 'linear-gradient(150deg, #1E1B4B 0%, #312E81 48%, #1E3A8A 100%)',
  /** AI-specific, for Stratify Intelligence surfaces only. */
  ai: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
} as const;

// ── Radius (§07) ───────────────────────────────────────────────────────────
/** Restrained. Not everything is a pill. */
export const RADIUS = {
  input: 10,
  button: 10,
  card: 14,
  container: 18,
  dialog: 18,
  pill: 999,
} as const;

// ── Spacing (§06) ──────────────────────────────────────────────────────────
/** The 4/8 rhythm. Use these, never an arbitrary number. */
export const SPACE = [4, 8, 12, 16, 20, 24, 32, 40, 48, 64] as const;

// ── Elevation (§08) ────────────────────────────────────────────────────────
/** Borders and contrast do the work; shadows only hint. Nothing inflated. */
export const SHADOW = {
  none: 'none',
  hairline: '0 1px 2px rgba(15,23,42,0.05)',
  raised: '0 4px 16px -8px rgba(15,23,42,0.18)',
  float: '0 18px 40px -26px rgba(15,23,42,0.35)',
  /** Coloured lift, for a selected/active surface only. */
  primaryLift: '0 16px 34px -26px rgba(37,99,235,0.85)',
} as const;

// ── Motion (§36) ───────────────────────────────────────────────────────────
export const EASE = 'cubic-bezier(.16,1,.3,1)';
export const DURATION = { fast: '.15s', base: '.22s', slow: '.32s' } as const;

/** Wrap every transition so one reduced-motion check disables the lot. */
export const motion = (reduced: boolean, value: string) => (reduced ? 'none' : value);

/** Drop-in sx fragment: honours prefers-reduced-motion without a JS hook. */
export const reducedMotionSx = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };

// ── Status semantics ───────────────────────────────────────────────────────
export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'ai';

export const TONE_COLOR: Record<Tone, string> = {
  success: COLOR.success,
  warning: COLOR.warning,
  danger: COLOR.danger,
  info: COLOR.primary,
  neutral: COLOR.inkMuted,
  ai: COLOR.ai,
};

// ── Typography (§05, §48.7) ────────────────────────────────────────────────
/** Oversized type is reserved for page titles and headline business numbers.
 *  Everything operational stays compact — ERP users need density (§38). */
export const TYPE = {
  pageTitle: { fontSize: '2.25rem', fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.08 },
  sectionTitle: { fontSize: '1.125rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 },
  metric: { fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' as const },
  metricLarge: { fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' as const },
  /** Mono eyebrow, matching the shell and the landing page. */
  eyebrow: {
    fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '0.16em',
    textTransform: 'uppercase' as const,
  },
} as const;
